import { ReactNode, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Base, PlusIcon } from '@itsrever/design-system'
import { equalJSON } from '@/utils'
import {
  Condition,
  ConditionField,
  NEW_TEST_RETURN,
  RETURN_METHODS,
  RoutingRule,
  TestReturn,
  Warehouse,
  fieldSpec,
  isRuleComplete,
  matchesTest,
  newCondition,
  shortAddress,
  splitDefault,
  testFields,
  toConditions,
  toWhens
} from '@/domain/warehouses'
import {
  AddCondition,
  OpenKey,
  Option,
  OptionsPicker,
  RuleListFrame,
  RuleRow,
  SetOpenKey,
  bindOpen,
  countRuleChanges,
  newRuleKey,
  ruleStatus,
  savedKey,
  useNewRuleShortcut,
  useRuleOrder
} from '@/ui/components/rule-list'
import {
  ConditionPill,
  LockedPill,
  useFieldGroups,
  useValueOptions
} from './condition-pill'

const {
  Button,
  Callout,
  Card,
  Input,
  FieldRoot,
  FieldLabel,
  FieldDescription,
  ToggleGroup,
  ToggleGroupItem
} = Base

// The default is flagged rather than inferred: a new rule without conditions is not the default yet.
export type DraftRule = RoutingRule & { key: string; isDefault?: boolean }

export const toDraft = (rules: RoutingRule[]): DraftRule[] => {
  const { fallback } = splitDefault(rules)
  return rules.map((rule, i) => ({
    ...rule,
    key: savedKey(i),
    isDefault: !!fallback && i === rules.length - 1
  }))
}

export const fromDraft = (rules: DraftRule[]): RoutingRule[] =>
  rules.map(({ warehouseId, whens }) => ({ warehouseId, whens }))

const statusOf = (rule: DraftRule, saved: RoutingRule[]) =>
  ruleStatus(rule, saved, (r, original) =>
    equalJSON(fromDraft([r])[0], original)
  )

export const countChanges = (rules: DraftRule[], saved: RoutingRule[]) =>
  countRuleChanges(rules, saved.length, (rule) => statusOf(rule, saved))

export function countIncomplete(rules: DraftRule[]) {
  const fallback = rules.find((rule) => rule.isDefault)
  return (
    rules.filter((rule) => !isRuleComplete(rule, !!rule.isDefault)).length +
    (fallback ? 0 : 1)
  )
}

interface RuleListProps {
  rules: DraftRule[]
  saved: RoutingRule[]
  warehouses: Warehouse[]
  editable: boolean
  // Shown next to the rule count: who the rules apply to.
  status?: ReactNode
  onChange: (rules: DraftRule[]) => void
}

export const RuleList: React.FC<RuleListProps> = ({
  rules,
  saved,
  warehouses,
  editable,
  status,
  onChange
}) => {
  const { t } = useTranslation()
  const [openKey, setOpenKey] = useState<OpenKey>(null)
  const [test, setTest] = useState<TestReturn | null>(null)
  const body = rules.filter((rule) => !rule.isDefault)
  const fallback = rules.find((rule) => rule.isDefault)

  const setBody = (next: DraftRule[]) =>
    onChange(fallback ? [...next, fallback] : next)
  const update = (rule: DraftRule) =>
    onChange(rules.map((r) => (r.key === rule.key ? rule : r)))
  const rowProps = useRuleOrder(body, setBody)
  const addRule = () => {
    const key = newRuleKey()
    setBody([...body, { key, warehouseId: '', whens: [] }])
    setOpenKey(`${key}:add`)
  }
  useNewRuleShortcut(editable && !openKey, addRule)

  const matched = test ? body.findIndex((rule) => matchesTest(rule, test)) : -1

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <p className="text-sm text-neutral-500">
          {t('warehouses.rules.rule_count', { count: body.length })}
        </p>
        {status}
        <div className="ml-auto flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setTest(test ? null : NEW_TEST_RETURN)}
          >
            {t(
              test
                ? 'warehouses.rules.close_test'
                : 'warehouses.rules.test_return'
            )}
          </Button>
          {editable ? (
            <Button type="button" variant="primary" size="sm" onClick={addRule}>
              <PlusIcon color="currentColor" className="size-4" />
              {t('warehouses.rules.add_rule')}
            </Button>
          ) : null}
        </div>
      </div>
      {test ? (
        <TestPanel
          test={test}
          rules={rules}
          warehouses={warehouses}
          result={matched >= 0 ? body[matched] : fallback}
          resultLabel={
            matched >= 0
              ? t('warehouses.rules.rule_label', { n: matched + 1 })
              : t('warehouses.rules.all_other_returns')
          }
          onChange={setTest}
        />
      ) : null}
      <Callout tone="info" role="note" className="px-4 py-3 text-xs">
        <Trans
          i18nKey="warehouses.rules.order_banner"
          components={{
            b: <span className="font-medium text-neutral-900" />
          }}
        />
      </Callout>
      <RuleListFrame
        ns="warehouses.rules"
        outcome={{
          label: t('warehouses.rules.column_warehouse'),
          className: 'w-56'
        }}
        fallbackHighlighted={!!test && matched < 0}
        fallback={
          <WarehouseCell
            id="default"
            warehouseId={fallback?.warehouseId ?? ''}
            warehouses={warehouses}
            editable={editable}
            label={t('warehouses.rules.default_warehouse_label')}
            openKey={openKey}
            setOpenKey={setOpenKey}
            onPick={(warehouseId) =>
              onChange(
                fallback
                  ? rules.map((r) => (r.isDefault ? { ...r, warehouseId } : r))
                  : [
                      ...rules,
                      {
                        key: 'new-default',
                        warehouseId,
                        whens: [],
                        isDefault: true
                      }
                    ]
              )
            }
          />
        }
      >
        {body.map((rule, index) => (
          <RuleRow
            key={rule.key}
            {...rowProps(rule, index)}
            ns="warehouses.rules"
            status={statusOf(rule, saved)}
            highlighted={!!test && index === matched}
            dimmed={!!test && matched >= 0 && index > matched}
            editable={editable}
            outcome={
              <WarehouseCell
                id={rule.key}
                warehouseId={rule.warehouseId}
                warehouses={warehouses}
                editable={editable}
                label={t('warehouses.rules.warehouse_label', {
                  n: index + 1
                })}
                openKey={openKey}
                setOpenKey={setOpenKey}
                onPick={(warehouseId) => update({ ...rule, warehouseId })}
              />
            }
            onDelete={() => setBody(body.filter((r) => r.key !== rule.key))}
          >
            <RuleConditions
              rule={rule}
              n={index + 1}
              editable={editable}
              openKey={openKey}
              setOpenKey={setOpenKey}
              onChange={update}
            />
          </RuleRow>
        ))}
      </RuleListFrame>
      {editable ? (
        <p className="text-xs text-neutral-400">{t('warehouses.rules.hint')}</p>
      ) : null}
    </div>
  )
}

const RuleConditions: React.FC<{
  rule: DraftRule
  n: number
  editable: boolean
  openKey: OpenKey
  setOpenKey: SetOpenKey
  onChange: (rule: DraftRule) => void
}> = ({ rule, n, editable, openKey, setOpenKey, onChange }) => {
  const { t } = useTranslation()
  const fieldGroups = useFieldGroups()
  // Undefined for a rule the builder can't show: it stays as written and only its warehouse can change.
  const conditions = useMemo(() => toConditions(rule.whens), [rule.whens])
  const setConditions = (next: Condition[]) =>
    onChange({ ...rule, whens: toWhens(next) })

  if (!conditions) return <LockedPill />
  return (
    <>
      {conditions.map((condition, i) => (
        <span key={i} className="inline-flex max-w-full items-center gap-2">
          {i > 0 ? (
            <span className="text-xs text-neutral-400">
              {t('warehouses.rules.and')}
            </span>
          ) : null}
          <ConditionPill
            id={`${rule.key}-c${i}`}
            condition={condition}
            editable={editable}
            openKey={openKey}
            setOpenKey={setOpenKey}
            onChange={(next) =>
              setConditions(conditions.map((c, k) => (k === i ? next : c)))
            }
            onRemove={() => setConditions(conditions.filter((_, k) => k !== i))}
          />
        </span>
      ))}
      {editable ? (
        <AddCondition
          ns="warehouses.rules"
          n={n}
          empty={conditions.length === 0}
          groups={fieldGroups}
          searchLabel={t('warehouses.conditions.filter_by')}
          emptyText={t('warehouses.conditions.no_field')}
          width="w-64"
          {...bindOpen(`${rule.key}:add`, openKey, setOpenKey)}
          onPick={(field) => {
            setConditions([
              ...conditions,
              newCondition(field as ConditionField)
            ])
            setOpenKey(`${rule.key}-c${conditions.length}:value`)
          }}
        />
      ) : conditions.length === 0 ? (
        <span className="text-sm text-neutral-400">
          {t('warehouses.rules.always')}
        </span>
      ) : null}
    </>
  )
}

const WarehouseCell: React.FC<{
  id: string
  warehouseId: string
  warehouses: Warehouse[]
  editable: boolean
  label: string
  openKey: OpenKey
  setOpenKey: SetOpenKey
  onPick: (warehouseId: string) => void
}> = ({
  id,
  warehouseId,
  warehouses,
  editable,
  label,
  openKey,
  setOpenKey,
  onPick
}) => {
  const { t } = useTranslation()
  const warehouse = warehouses.find((w) => w.id === warehouseId)
  const options = warehouses.map((w) => ({
    value: w.id,
    label: [w.company || w.id, w.city].filter(Boolean).join(' · ')
  }))
  const triggerClassName =
    'inline-flex w-56 shrink-0 items-center rounded-md px-2 py-1 text-left text-sm font-medium outline-none ' +
    (editable ? 'hover:bg-neutral-100 focus-visible:bg-neutral-100 ' : '') +
    (warehouseId ? 'text-neutral-900' : 'text-red-500')
  const triggerContent = warehouseId ? (
    <span className="flex min-w-0 flex-col">
      <span className="truncate">{warehouse?.company || warehouseId}</span>
      {warehouse ? (
        <span className="truncate text-xs font-normal text-neutral-500">
          {shortAddress(warehouse)}
        </span>
      ) : null}
    </span>
  ) : (
    t('warehouses.rules.choose_warehouse')
  )

  if (!editable)
    return <span className={triggerClassName}>{triggerContent}</span>
  return (
    <OptionsPicker
      options={options}
      value={warehouseId ? [warehouseId] : []}
      onChange={([picked]) => picked && onPick(picked)}
      {...bindOpen(`${id}:warehouse`, openKey, setOpenKey)}
      triggerLabel={label}
      triggerClassName={triggerClassName}
      triggerContent={triggerContent}
    />
  )
}

const TestPanel: React.FC<{
  test: TestReturn
  rules: RoutingRule[]
  warehouses: Warehouse[]
  result: RoutingRule | undefined
  resultLabel: string
  onChange: (test: TestReturn) => void
}> = ({ test, rules, warehouses, result, resultLabel, onChange }) => {
  const { t } = useTranslation()
  const warehouse = warehouses.find((w) => w.id === result?.warehouseId)

  return (
    <Card variant="muted" className="flex flex-col gap-5 p-5">
      <div className="flex flex-wrap gap-8">
        {testFields(rules).map((field) => (
          <TestField
            key={field}
            field={field}
            rules={rules}
            value={test[field] ?? ''}
            onChange={(value) => onChange({ ...test, [field]: value })}
          />
        ))}
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-sm text-neutral-700">
          <Trans
            i18nKey="warehouses.rules.test_result"
            values={{
              rule: resultLabel,
              warehouse:
                warehouse?.company || t('warehouses.rules.no_warehouse')
            }}
            components={{ b: <span className="font-medium" /> }}
          />
        </p>
        <p className="text-xs text-neutral-400">
          {t('warehouses.rules.test_note')}
        </p>
      </div>
    </Card>
  )
}

// Fields with a known set of values are chips; the rest are typed.
const TestField: React.FC<{
  field: ConditionField
  rules: RoutingRule[]
  value: string
  onChange: (value: string) => void
}> = ({ field, rules, value, onChange }) => {
  const { t } = useTranslation()
  const optionsFor = useValueOptions()
  const { kind, values: source } = fieldSpec(field)
  const label = t(`warehouses.conditions.fields.${field}`)
  // Every value the rules mention, so the test can reach each one, then a few common ones.
  const pick = (source: 'countries' | 'returnReasons', defaults: string[]) => {
    const used = rules.flatMap((rule) =>
      (toConditions(rule.whens) ?? []).flatMap((c) =>
        c.field === field ? c.values : []
      )
    )
    const options = optionsFor(source, [])
    return [...new Set([...used, ...defaults])]
      .map((v) => options.find((option) => option.value === v))
      .filter((option): option is Option => option !== undefined)
  }

  const chips = (options: Option[]) => (
    <Chips label={label} options={options} value={value} onChange={onChange} />
  )
  if (kind === 'boolean')
    return chips([
      { value: 'true', label: t('warehouses.rules.test_yes') },
      { value: 'false', label: t('warehouses.rules.test_no') }
    ])
  if (source === 'returnMethods')
    return chips(
      optionsFor('returnMethods', []).filter((o) =>
        RETURN_METHODS.includes(o.value)
      )
    )
  if (source === 'returnReasons')
    return chips(pick('returnReasons', ['CHANGED_MY_MIND', 'DEFECTIVE']))
  if (source === 'countries' || source === 'returnCountries')
    return chips(pick('countries', ['ES', 'FR', 'DE', 'GB', 'US']))

  return (
    <FieldRoot className="w-48">
      <FieldLabel>{label}</FieldLabel>
      <Input
        type={kind === 'date' ? 'date' : 'text'}
        inputMode={
          kind === 'money'
            ? 'decimal'
            : kind === 'number'
              ? 'numeric'
              : undefined
        }
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {kind === 'money' || kind === 'list' ? (
        <FieldDescription>
          {t(
            kind === 'money'
              ? 'rule_list.money_hint'
              : 'warehouses.rules.test_several'
          )}
        </FieldDescription>
      ) : null}
    </FieldRoot>
  )
}

const Chips: React.FC<{
  label: string
  options: Option[]
  value: string
  onChange: (value: string) => void
}> = ({ label, options, value, onChange }) => (
  <div className="flex flex-col gap-1.5">
    <p className="text-xs text-neutral-500">{label}</p>
    <ToggleGroup
      variant="button"
      aria-label={label}
      value={[value]}
      // Pressing the picked option again clears it, like a return without that field.
      onValueChange={([next]: string[]) => onChange(next ?? '')}
    >
      {options.map((option) => (
        <ToggleGroupItem key={option.value} value={option.value}>
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  </div>
)
